/**
 * Toast 알림 시스템
 * 화면 우측 하단에 임시 알림을 표시합니다.
 */

type ToastType = 'success' | 'error' | 'info';

interface ActiveToast {
  id: number;
  el: HTMLElement;
  timer: number;
}

let toastId = 0;
let container: HTMLElement | null = null;
const activeToasts: ActiveToast[] = [];
const MAX_TOASTS = 3;

function getContainer(): HTMLElement {
  if (!container) {
    container = document.createElement('div');
    container.id = 'toast-container';
    container.className = 'fixed bottom-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none';
    container.setAttribute('aria-live', 'polite');
    container.setAttribute('aria-label', '알림');
    document.body.appendChild(container);
  }
  return container;
}

function removeToastItem(id: number): void {
  const idx = activeToasts.findIndex(t => t.id === id);
  if (idx >= 0) {
    const item = activeToasts[idx];
    clearTimeout(item.timer);
    item.el.classList.add('toast-exit');
    setTimeout(() => item.el.remove(), 300);
    activeToasts.splice(idx, 1);
  }
}

/**
 * Toast 알림 표시
 * @param message 알림 메시지
 * @param type success | error | info
 * @param duration 표시 시간 (ms, 기본 3000)
 */
export function toast(message: string, type: ToastType = 'info', duration: number = 3000): void {
  const key = `${type}:${message}`;

  // 동일 메시지가 이미 있으면 갱신 (맨 뒤로, 타이머 리셋)
  const existing = activeToasts.find(t => t.el.dataset.toastKey === key);
  if (existing) {
    clearTimeout(existing.timer);
    // 맨 뒤로 이동
    getContainer().appendChild(existing.el);
    const idx = activeToasts.indexOf(existing);
    activeToasts.splice(idx, 1);
    activeToasts.push(existing);
    existing.timer = window.setTimeout(() => removeToastItem(existing.id), duration);
    return;
  }

  // 최대 개수 초과 시 가장 오래된 것 제거
  if (activeToasts.length >= MAX_TOASTS) {
    removeToastItem(activeToasts[0].id);
  }

  const id = ++toastId;
  const el = document.createElement('div');
  el.dataset.toastKey = key;

  const colors: Record<ToastType, string> = {
    success: 'bg-kalpha-500 text-white',
    error: 'bg-red-500 text-white',
    info: 'bg-gray-800 text-white',
  };

  el.className = `${colors[type]} px-4 py-3 rounded-xl shadow-lg text-sm font-medium pointer-events-auto toast-enter flex items-center gap-2`;
  el.innerHTML = `
    ${type === 'success' ? '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>' : ''}
    ${type === 'error' ? '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>' : ''}
    ${message}
  `;

  getContainer().appendChild(el);

  const timer = window.setTimeout(() => removeToastItem(id), duration);
  activeToasts.push({ id, el, timer });
}
